import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch } from 'react-redux';
import { login } from 'thunks/authSliceThunks/thunk';
import { useNavigate, useLocation } from 'react-router-dom';
import useCredentials from 'hooks/useCredentials';
import { getRedirectUrl, getLastVisitedUrl } from '../../utils/sessionUtils';
import dashboardImg from 'assets/img-dashboard.png';
import wavingHandImg from 'assets/waving-hand.png';

const Login = () => {
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { isAuthenticated, loading, error: authError } = useCredentials();

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleMouseDownPassword = (event) => event.preventDefault();

  const validate = () => {
    if (!userName.trim() || !password.trim()) {
      toast.error('Please fill in all fields correctly.');
      return false;
    }
    return true;
  };

  const handleLogin = (event) => {
    event.preventDefault(); // Prevent page reload on form submit
    if (!validate()) return;
    dispatch(login({ username: userName, password }));
  };

  useEffect(() => {
    if (isAuthenticated) {
      toast.success('Login Successful!');

      // Priority order for redirect:
      // 1. Stored redirect URL (from session expiration)
      // 2. URL parameter redirect
      // 3. Location state from
      // 4. Last visited URL
      // 5. Default to home page

      let redirectPath = null;

      // Check for stored redirect URL first (session expiration)
      const storedRedirectUrl = getRedirectUrl();
      if (storedRedirectUrl) {
        redirectPath = storedRedirectUrl;
      } else {
        // Check for redirect parameter in URL
        const urlParams = new URLSearchParams(window.location.search);
        const urlRedirectPath = urlParams.get('redirect');

        if (urlRedirectPath) {
          // Clear the redirect parameter from URL
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);

          // Validate the redirect path to prevent open redirects
          const decodedPath = decodeURIComponent(urlRedirectPath);
          if (decodedPath.startsWith('/') && !decodedPath.startsWith('//')) {
            redirectPath = decodedPath;
          }
        } else if (location.state?.from) {
          // Use location state
          redirectPath = location.state.from;
        } else {
          // Use last visited URL
          redirectPath = getLastVisitedUrl();
        }
      }

      // Navigate to the determined path
      console.log('Final redirect path:', redirectPath); // Debug log
      navigate(redirectPath || '/', { replace: true });
    }
  }, [isAuthenticated, navigate, location.state?.from]);

  useEffect(() => {
    if (authError) {
      toast.error(authError);
    }
  }, [authError]);

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh' }}>
      {/* Left Section */}
      <Box
        sx={{
          flex: { xs: 'none', md: '0 0 58.33%' },
          background: 'linear-gradient(to bottom, #E9FCE0 0%, #E5E8EE 100%)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          position: 'relative',
          minHeight: { md: '100vh' },
        }}
      >
        <Box sx={{ position: 'absolute', top: '5%', left: '10%' }}>
          <Box sx={{ fontSize: 40, color: '#110B2C', fontWeight: 700, mb: 1 }}>Satin Learn</Box>
          <Box sx={{ fontSize: 18, fontWeight: 500, color: '#706C84' }}></Box>
        </Box>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <img
            src={dashboardImg}
            alt="dash-img"
            style={{ width: '80%', height: 'auto', maxWidth: '550px' }}
          />
        </Box>
      </Box>

      {/* Right Section */}
      <Box
        sx={{
          flex: { xs: '1', md: '0 0 41.67%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: { xs: '100vh', md: 'auto' },
          px: { xs: 3, sm: 4 },
          py: { xs: 4, md: 0 },
        }}
      >
        {/* Mobile brand header */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4, textAlign: 'center' }}>
          <Box sx={{ fontSize: 32, color: '#110B2C', fontWeight: 700 }}>Satin Learn</Box>
        </Box>
        <form onSubmit={handleLogin} style={{ width: '100%', maxWidth: '380px' }}>
          <Box>
            <Box sx={{ fontSize: '26px', fontWeight: 700 }}>
              Hello! <img src={wavingHandImg} alt="hello" />
            </Box>
            <Box sx={{ fontSize: '18px', fontWeight: 400 }}>
              Use your HRMS Credentials to Login.
            </Box>

            {/* Username Field */}
            <Box className="mt-3">
              <TextField
                fullWidth
                label="Username"
                size="small"
                onChange={(e) => setUserName(e.target.value)}
                value={userName}
                autoFocus
              />
            </Box>

            {/* Password Field */}
            <Box className="mt-1 mb-0" style={{ marginBottom: '-15px' }}>
              <TextField
                fullWidth
                label="Password"
                size="small"
                type={showPassword ? 'text' : 'password'}
                onChange={(e) => setPassword(e.target.value)}
                value={password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Sign In Button */}
            <Box className="mt-3">
              <Button
                variant="contained"
                size="medium"
                sx={{
                  borderRadius: 50,
                  background: '#2800B1',
                  fontSize: 12,
                  border: '1px solid #2800B1',
                  width: '100%',
                }}
                type="submit"
                disabled={loading}
              >
                {loading ? <CircularProgress size={23} color="inherit" /> : 'Sign In'}
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default Login;
